import { WidgetInputToggleField } from '@/ui/rapid-ui/inputs/widgets/widget-input-toggle-field'
import { Link } from '@tanstack/react-router'
import { Trans, useTranslation } from 'react-i18next'
import { useWhatsAppAiAgentToggle } from './use-whatsapp-ai-agent-toggle'

interface WhatsAppAiConfigPanelProps {
	companyId: string
	campaignId: string
}

export function WhatsAppAiConfigPanel({ companyId, campaignId }: WhatsAppAiConfigPanelProps) {
	const { t } = useTranslation('common')
	const {
		isLoading,
		isLoadError,
		consentRequired,
		enabled,
		isEnabling,
		isDisabling,
		hasError,
		handleEnable,
		handleDisable,
	} = useWhatsAppAiAgentToggle(campaignId)

	if (isLoading) {
		return (
			<div className="flex items-center justify-center min-h-64 text-sm text-grey-400">
				{t('campaign.settings.whatsappAiAgent.loading')}
			</div>
		)
	}

	if (isLoadError) {
		return (
			<div className="flex items-center justify-center min-h-64 text-sm text-red-500">
				{t('campaign.settings.whatsappAiAgent.loadError')}
			</div>
		)
	}

	if (consentRequired) {
		return (
			<div className="max-w-2xl">
				<h2 className="mb-1 text-lg font-semibold text-grey-900">
					{t('campaign.settings.whatsappAiAgent.title')}
				</h2>
				<p className="text-sm text-grey-500">
					<Trans
						i18nKey="campaign.settings.whatsappAiAgent.consentRequired"
						ns="common"
						components={{
							aiSettingsLink: (
								<Link
									to="/company/$companyId/settings/ai"
									params={{ companyId }}
									className="underline hover:text-grey-700"
								/>
							),
						}}
					/>
				</p>
			</div>
		)
	}

	const isPending = isEnabling || isDisabling
	const statusLabel = isEnabling
		? t('campaign.settings.whatsappAiAgent.actions.enabling')
		: isDisabling
		? t('campaign.settings.whatsappAiAgent.actions.disabling')
		: enabled
		? t('campaign.settings.whatsappAiAgent.status.enabled')
		: t('campaign.settings.whatsappAiAgent.status.disabled')

	return (
		<div className="max-w-2xl space-y-8">
			<div>
				<h2 className="mb-1 text-lg font-semibold text-grey-900">
					{t('campaign.settings.whatsappAiAgent.title')}
				</h2>
				<p className="text-sm text-grey-500">{t('campaign.settings.whatsappAiAgent.subtitle')}</p>
				<p className="mt-1 text-sm text-grey-500">{t('campaign.settings.whatsappAiAgent.introNotice')}</p>
			</div>

			<div className="space-y-2">
				<div className="flex items-center gap-3">
					<WidgetInputToggleField
						value={enabled}
						onChange={(next) => (next ? handleEnable() : handleDisable())}
						disabled={isPending}
						ariaLabel={t('campaign.settings.whatsappAiAgent.title')}
					/>
					<span className="text-sm font-medium text-grey-700">{statusLabel}</span>
				</div>

				{!enabled && !isPending
					? (
						<span className="block text-xs text-grey-400">
							{t('campaign.settings.whatsappAiAgent.actions.enableHint')}
						</span>
					)
					: null}
				{hasError
					? (
						<span className="block text-xs text-red-500">
							{t('campaign.settings.whatsappAiAgent.errorGeneric')}
						</span>
					)
					: null}
			</div>
		</div>
	)
}
